import axios from 'axios';
import { getApiBaseUrl, getMediaBaseUrl } from '../utils/runtimeUrls';

const baseURL = getApiBaseUrl();
export { baseURL as API_BASE_URL };

console.log('🌐 API Configuration:', {
  nodeEnv: process.env.NODE_ENV,
  baseURL: baseURL,
  reactAppApiUrl: process.env.REACT_APP_API_URL
});

const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const redactSecrets = (value, depth = 0, seen = new WeakSet()) => {
  if (depth > 6) return '[MaxDepth]';
  if (value === null || value === undefined) return value;

  // Evita di loggare file/multipart
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return '[FormData]';
  }

  // Skip DOM nodes, Window, and other browser objects
  if (typeof window !== 'undefined' && value === window) {
    return '[Window]';
  }
  if (value instanceof Element || value instanceof Document) {
    return '[DOMElement]';
  }

  if (Array.isArray(value)) {
    return value.map((v) => redactSecrets(v, depth + 1, seen));
  }

  if (typeof value === 'object') {
    // Detect circular references
    if (seen.has(value)) {
      return '[Circular]';
    }
    seen.add(value);

    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const key = String(k).toLowerCase();
      if (key.includes('password') || key.includes('pass') || key.includes('secret')) {
        out[k] = '***';
      } else {
        out[k] = redactSecrets(v, depth + 1, seen);
      }
    }
    return out;
  }

  return value;
};

// Interceptor per aggiungere token e debug
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('📤 Request:', config.method?.toUpperCase(), config.url, redactSecrets(config.data));
    return config;
  },
  (error) => {
    console.error('📤 Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor per gestire risposte e errori
api.interceptors.response.use(
  (response) => {
    console.log('📥 Response:', response.status, response.config.url, redactSecrets(response.data));
    return response;
  },
  (error) => {
    console.error('📥 Response Error:', error.response?.status, error.config?.url, error.response?.data);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Clienti
export const customersApi = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  createDraft: () => api.post('/customers/draft'),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  deletePermanent: (id) => api.delete(`/customers/${id}/permanent`),
  search: (query) => api.get('/customers/search', { params: { q: query } }),
  getContracts: (id) => api.get(`/customers/${id}/contracts`),
  getInvoices: (id) => api.get(`/customers/${id}/invoices`),
  getBalance: (id) => api.get(`/customers/${id}/balance`),
  // Interventi extra
  getInterventions: (id, params) => api.get(`/customers/${id}/interventions`, { params }),
  getPendingInterventions: (id) => api.get(`/customers/${id}/interventions/pending`),
  createIntervention: (id, data) => api.post(`/customers/${id}/interventions`, data),
  updateIntervention: (customerId, interventionId, data) => api.put(`/customers/${customerId}/interventions/${interventionId}`, data),
  deleteIntervention: (customerId, interventionId) => api.delete(`/customers/${customerId}/interventions/${interventionId}`),
  // Comunicazioni email
  getCommunications: (id, params) => api.get(`/customers/${id}/communications`, { params }),
  // Estratto conto
  getAccountStatementPdf: (id, fromInvoiceId = null) => {
    const params = fromInvoiceId ? { fromInvoiceId } : {};
    return api.get(`/customers/${id}/account-statement/pdf`, { params, responseType: 'blob' });
  },
  sendAccountStatement: (id, email, fromInvoiceId = null) => api.post(`/customers/${id}/account-statement/send`, { email, fromInvoiceId }),
  // Contratto GPL
  getContractPdf: (id, meterId = null) => {
    const params = meterId ? { meter_id: meterId } : {};
    return api.get(`/customers/${id}/contract/pdf`, { params, responseType: 'blob' });
  },
  sendContract: (id, meterId = null, email = null) => api.post(`/customers/${id}/contract/send`, { meter_id: meterId, email }),
  getMeters: (id) => api.get(`/customers/${id}/meters`),
  // Piani di rateizzazione
  getInstallmentPlans: (id) => api.get(`/customers/${id}/installment-plans`),
  createInstallmentPlan: (id, data) => api.post(`/customers/${id}/installment-plans`, data, { responseType: 'blob' }),
  getInstallmentPlanPdf: (id, planId) => api.get(`/customers/${id}/installment-plans/${planId}/pdf`, { responseType: 'blob' }),
  updateInstallmentPlan: (id, planId, data) => api.put(`/customers/${id}/installment-plans/${planId}`, data),
  deleteInstallmentPlan: (id, planId) => api.delete(`/customers/${id}/installment-plans/${planId}`),
  sendInstallmentPlanEmail: (id, planId, data) => api.post(`/customers/${id}/installment-plans/${planId}/send`, data),
  getInstallmentPlanEmailPreview: (id, planId) => api.get(`/customers/${id}/installment-plans/${planId}/email-preview`),
  // Note cliente
  getNotes: (id) => api.get(`/customers/${id}/notes`),
  createNote: (id, data) => api.post(`/customers/${id}/notes`, data),
  updateNote: (id, noteId, data) => api.put(`/customers/${id}/notes/${noteId}`, data),
  deleteNote: (id, noteId) => api.delete(`/customers/${id}/notes/${noteId}`),
  getAiSummary: (id) => api.get(`/customers/${id}/ai-summary`),
  refreshAiSummary: (id) => api.post(`/customers/${id}/ai-summary/refresh`),
};

// API Documenti Cliente
export const customerDocumentsApi = {
  getAll: (customerId) => api.get(`/customer-documents/${customerId}`),
  upload: (customerId, file, description) => {
    const formData = new FormData();
    formData.append('document', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post(`/customer-documents/${customerId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAndOcr: (customerId, file, description) => {
    const formData = new FormData();
    formData.append('document', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post(`/customer-documents/${customerId}/upload-and-ocr`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  extractOcr: (customerId, documentId) => 
    api.post(`/customer-documents/${customerId}/ocr/${documentId}`),
  download: (customerId, documentId) => 
    api.get(`/customer-documents/${customerId}/download/${documentId}`, { responseType: 'blob' }),
  view: (customerId, documentId) => 
    api.get(`/customer-documents/${customerId}/view/${documentId}`, { responseType: 'blob' }),
  delete: (customerId, documentId) => 
    api.delete(`/customer-documents/${customerId}/${documentId}`),
  update: (customerId, documentId, data) => 
    api.put(`/customer-documents/${customerId}/${documentId}`, data),
  saveFromEmail: (customerId, emailId, attachmentId, description) =>
    api.post(`/customer-documents/${customerId}/from-email-attachment`, { emailId, attachmentId, description }),
};

// API Contratti
export const contractsApi = {
  getAll: (params) => api.get('/contracts', { params }),
  getById: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  getExpiring: (days = 30) => api.get('/contracts/expiring', { params: { days } }),
};

// API Fatture
export const invoicesApi = {
  getCreditNoteDraft: (id) => api.get(`/invoices/${id}/credit-notes/draft`),
  previewCreditNote: (id, data, config) => api.post(`/invoices/${id}/credit-notes/preview`, data, config),
  createCreditNote: (id, data) => api.post(`/invoices/${id}/credit-notes`, data),
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getDeletePreview: (id) => api.get(`/invoices/${id}/delete-preview`),
  deletePermanent: (id) => api.delete(`/invoices/${id}/permanent`),
  getOverdue: (params) => api.get('/invoices/overdue', { params }),
  getBalance: (id) => api.get(`/invoices/${id}/balance`),
  getPaymentsHistory: (id) => api.get(`/invoices/${id}/payments-history`),
  generatePdf: (id, { layout = 'standard' } = {}) => api.get(`/invoices/${id}/pdf`, { params: { layout }, responseType: 'blob' }),
  downloadAttachment: (id, attachmentType) => api.get(`/invoices/${id}/attachments/${attachmentType}`, { responseType: 'blob' }),
  sendEmail: (id, customData) => api.post(`/invoices/${id}/send`, customData || {}),
  sendWhatsapp: (id, data) => api.post(`/invoices/${id}/send-whatsapp`, data || {}),
  
  // Invio massivo email
  sendBulkPreview: (filters) => api.post('/invoices/send-bulk/preview', filters),
  sendBulkExecute: (data) => api.post('/invoices/send-bulk/execute', data),
  getEmailPreview: (id) => api.get(`/invoices/${id}/email-preview`),
  getWhatsappPreview: (id) => api.get(`/invoices/${id}/whatsapp-preview`),
  
  // Export PDF massivo
  exportPdfBatch: (filters) => api.post('/invoices/export-pdf-batch', filters, { responseType: 'blob' }),
  
  // Solleciti e promemoria pagamento
  getRemindersPreview: (type, params) => api.get(
    type === 'reminder' ? '/invoices/reminders/preview' : '/invoices/disconnection-notices/preview',
    { params }
  ),
  sendReminders: (type, customerIds, params) => api.post(
    type === 'reminder' ? '/invoices/reminders/send' : '/invoices/disconnection-notices/send',
    { customerIds, ...params }
  ),
  sendRemindersWhatsApp: (type, customerIds, params) => api.post(
    type === 'reminder' ? '/invoices/reminders/send-whatsapp' : '/invoices/disconnection-notices/send-whatsapp',
    { customerIds, ...params }
  ),
  generateRemindersPdf: (type, customerIds, params) => api.post(
    '/invoices/reminders/generate-pdf',
    { customerIds, type: type === 'reminder' ? 'reminder' : 'disconnection', ...params },
    { responseType: 'blob' }
  ),
  getRemindersHistory: (params) => api.get('/invoices/reminders/history', { params }),
  getRemindersSettings: () => api.get('/invoices/reminders/settings'),
  updateRemindersSettings: (data) => api.put('/invoices/reminders/settings', data),

  // SEPA Direct Debit
  exportSepaPreview: (filters) => api.post('/invoices/export-sepa/preview', filters),
  exportSepa: (data) => api.post('/invoices/export-sepa', data),
};

// API Pagamenti
export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  getOpenInvoices: (params) => api.get('/payments/invoices/open', { params }),
};

// API Acconti
export const advancesApi = {
  getAll: (params) => api.get('/advances', { params }),
  getById: (id) => api.get(`/advances/${id}`),
  create: (data) => api.post('/advances', data),
  update: (id, data) => api.put(`/advances/${id}`, data),
  delete: (id) => api.delete(`/advances/${id}`),
};

// API Riconciliazione Bancaria
export const bankStatementsApi = {
  getMovements: (params) => api.get('/bank-statements/movements', { params }),
  previewMovements: (data) => api.post('/bank-statements/movements/batch-preview', data),
  processMovements: (data) => api.post('/bank-statements/movements/batch', data),
  analyzeMovement: (rowId) => api.post(`/bank-statements/movements/${rowId}/analyze`),
  assignMovement: (rowId, assignments) => api.post(`/bank-statements/movements/${rowId}/assign`, { assignments }),
  confirmMovement: (rowId) => api.post(`/bank-statements/movements/${rowId}/confirm`),
  ignoreMovement: (rowId, note) => api.post(`/bank-statements/movements/${rowId}/ignore`, { note }),
  editMovement: (rowId) => api.post(`/bank-statements/movements/${rowId}/edit`),
  getAll: (params) => api.get('/bank-statements', { params }),
  getById: (id) => api.get(`/bank-statements/${id}`),
  upload: (data) => api.post('/bank-statements/upload', data),
  delete: (id) => api.delete(`/bank-statements/${id}`),
  download: (id) => api.get(`/bank-statements/${id}/download`, { responseType: 'blob' }),
  // Righe
  getRows: (id, params) => api.get(`/bank-statements/${id}/rows`, { params }),
  analyzeRow: (statementId, rowId) => api.post(`/bank-statements/${statementId}/rows/${rowId}/analyze`),
  analyzeAll: (id, reprocess = true) => api.post(`/bank-statements/${id}/analyze-all?reprocess=${reprocess}`),
  assignRow: (statementId, rowId, assignments) => api.post(`/bank-statements/${statementId}/rows/${rowId}/assign`, { assignments }),
  confirmRow: (statementId, rowId) => api.post(`/bank-statements/${statementId}/rows/${rowId}/confirm`),
  ignoreRow: (statementId, rowId, note) => api.post(`/bank-statements/${statementId}/rows/${rowId}/ignore`, { note }),
  editRow: (statementId, rowId) => api.post(`/bank-statements/${statementId}/rows/${rowId}/edit`),
  confirmAll: (id, minConfidence = 85) => api.post(`/bank-statements/${id}/confirm-all`, { minConfidence }),
};

// API Bollettini Postali
export const postalSlipsApi = {
  getOcrOverlay: (id, imageHash) => api.post(`/postal-slips/${id}/ocr-overlay`, { image_hash: imageHash }),
  getAssignments: (id) => api.get(`/postal-slips/${id}/assignments`),
  getIntegrity: () => api.get('/postal-slips/integrity'),
  getAll: (params) => api.get('/postal-slips', { params }),
  getBatches: () => api.get('/postal-slips/batches'),
  getStats: () => api.get('/postal-slips/stats'),
  uploadFolder: (folderPath) => api.post('/postal-slips/upload-folder', { folderPath }),
  uploadZip: (formData) => api.post('/postal-slips/upload-zip', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  }),
  deleteBatch: (batchId) => api.delete(`/postal-slips/batch/${batchId}`),
  runOcr: (id) => api.post(`/postal-slips/${id}/ocr`),
  findMatch: (id) => api.post(`/postal-slips/${id}/match`),
  processAll: (batchId, reprocess = true) => api.post(`/postal-slips/batch/${batchId}/process-all?reprocess=${reprocess}`),
  confirm: (id) => api.post(`/postal-slips/${id}/confirm`),
  ignore: (id, note) => api.post(`/postal-slips/${id}/ignore`, { note }),
  update: (id, data) => api.put(`/postal-slips/${id}`, data),
  assign: (id, data) => api.post(`/postal-slips/${id}/assign`, data),
  getImage: (id) => `/api/postal-slips/${id}/image`, // URL diretto per img src
};

// API Serbatoi
export const tanksApi = {
  getAll: (params) => api.get('/tanks', { params }),
  getById: (id) => api.get(`/tanks/${id}`),
  create: (data) => api.post('/tanks', data),
  update: (id, data) => api.put(`/tanks/${id}`, data),
  delete: (id) => api.delete(`/tanks/${id}`),
  getExpiring: (days = 90) => api.get('/tanks/expiring', { params: { days } }),
};

// ============================================
// API SISTEMA RIFORNIMENTI
// ============================================

// API Fornitori GPL
export const suppliersApi = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  addPlanner: (id, data) => api.post(`/suppliers/${id}/planners`, data),
  getInvoices: (id, params) => api.get(`/suppliers/${id}/invoices`, { params }),
};

// API Pianificatori
export const plannersApi = {
  getAll: (params) => api.get('/planners', { params }),
  getById: (id) => api.get(`/planners/${id}`),
  create: (data) => api.post('/planners', data),
  update: (id, data) => api.put(`/planners/${id}`, data),
  delete: (id) => api.delete(`/planners/${id}`),
  suggest: (plantId) => api.get(`/planners/suggest/${plantId}`),
  assignPlants: (id, plantIds) => api.post(`/planners/${id}/assign-plants`, { plant_ids: plantIds }),
};

// API Ordini GPL
export const gplOrdersApi = {
  getAll: (params) => api.get('/gpl-orders', { params }),
  getById: (id) => api.get(`/gpl-orders/${id}`),
  create: (data) => api.post('/gpl-orders', data),
  update: (id, data) => api.put(`/gpl-orders/${id}`, data),
  delete: (id) => api.delete(`/gpl-orders/${id}`),
  deletePermanent: (id) => api.delete(`/gpl-orders/${id}/permanent`),
  getUnlinked: () => api.get('/gpl-orders/unlinked'),
  sendEmail: (id) => api.post(`/gpl-orders/${id}/send-email`),
  linkToDelivery: (id, deliveryId) => api.post(`/gpl-orders/${id}/link`, { delivery_id: deliveryId }),
  // Nuovi metodi per riconciliazione
  getReconciliationStats: () => api.get('/gpl-orders/reconciliation/stats'),
  unlinkOrder: (id) => api.post(`/gpl-orders/${id}/unlink`),
  // Invio bulk email ordini
  sendBulkPreview: (orderIds) => api.post('/gpl-orders/send-bulk-preview', { order_ids: orderIds }),
  sendBulk: (orderIds, emailOverrides = {}) => api.post('/gpl-orders/send-bulk', { order_ids: orderIds, email_overrides: emailOverrides }),
};

// API Rifornimenti
export const deliveriesApi = {
  getAll: (params) => api.get('/deliveries', { params }),
  getById: (id) => api.get(`/deliveries/${id}`),
  create: (data) => api.post('/deliveries', data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  delete: (id) => api.delete(`/deliveries/${id}`),
  getUnlinked: () => api.get('/deliveries/unlinked'),
  linkToOrder: (id, orderId) => api.post(`/deliveries/${id}/link`, { order_id: orderId }),
  getMatchSuggestions: (orderId) => api.get(`/deliveries/match-suggestions/${orderId}`),
};

// API Fatture Fornitore
export const supplierInvoicesApi = {
  getAll: (params) => api.get('/supplier-invoices', { params }),
  getById: (id) => api.get(`/supplier-invoices/${id}`),
  create: (data) => api.post('/supplier-invoices', data),
  update: (id, data) => api.put(`/supplier-invoices/${id}`, data),
  delete: (id) => api.delete(`/supplier-invoices/${id}`),
  checkDuplicate: (data) => api.post('/supplier-invoices/check-duplicate', data),
  importXml: (data) => api.post('/supplier-invoices/import-xml', data),
  registerPayment: (id, data) => api.post(`/supplier-invoices/${id}/payment`, data),
  downloadXml: (id) => api.get(`/supplier-invoices/${id}/xml`, { responseType: 'blob' }),
  // Parse XML content senza importare
  parse: async (xmlContent, fileName) => {
    const response = await api.post('/supplier-invoices/parse-xml', { xmlContent, fileName });
    return response.data;
  },
  // Importa fattura già parsata
  import: async (parsedData) => {
    const response = await api.post('/supplier-invoices/import', parsedData);
    return response.data;
  },
};

// ============================================
// API SISTEMA IMPIANTI
// ============================================

// API Impianti
export const plantsApi = {
  getAll: (params) => api.get('/plants', { params }),
  getById: (id) => api.get(`/plants/${id}`),
  create: (data) => api.post('/plants', data),
  update: (id, data) => api.put(`/plants/${id}`, data),
  delete: (id) => api.delete(`/plants/${id}`),
  getLowLevel: () => api.get('/plants/low-level'),
  setLevel: (id, data) => api.post(`/plants/${id}/set-level`, data),
  setInitialLevel: (id, data) => api.post(`/plants/${id}/set-initial-level`, data),
  recalculateLevel: (id) => api.post(`/plants/${id}/recalculate-level`),
  getLevelDetails: (id) => api.get(`/plants/${id}/level-details`),
  getLevelInfo: (id) => api.get(`/plants/${id}/level-info`),
  getConsumptionForecast: (id) => api.get(`/plants/${id}/consumption-forecast`),
  getLevelHistory: (id, params) => api.get(`/plants/${id}/level-history`, { params }),
  getDeliveries: (id, params) => api.get(`/plants/${id}/deliveries`, { params }),
  getContracts: (id) => api.get(`/plants/${id}/contracts`),
  linkContract: (id, contractId) => api.post(`/plants/${id}/link-contract`, { contract_id: contractId }),
  unlinkContract: (id, contractId) => api.post(`/plants/${id}/unlink-contract`, { contract_id: contractId }),
  findByDeliveryCode: (deliveryCode) => api.post('/plants/find-by-delivery-code', { delivery_code: deliveryCode }),
  getConsumption: (id, params) => api.get(`/plants/${id}/consumption`, { params }),
  getConsumptionPdf: (id, params) => api.get(`/plants/${id}/consumption-pdf`, { params, responseType: 'blob' }),
  getDeliveriesGrouped: (id, params) => api.get(`/plants/${id}/deliveries-grouped`, { params }),
  getDeliveriesGroupedPdf: (id, params) => api.get(`/plants/${id}/deliveries-grouped-pdf`, { params, responseType: 'blob' }),
  // Rilevazioni tecnici
  getReadings: (id) => api.get(`/plants/${id}/readings`),
  addReading: (id, data) => api.post(`/plants/${id}/add-reading`, data),
  deleteReading: (id, readingId) => api.delete(`/plants/${id}/readings/${readingId}`),
  // Stima livello corrente (partendo da ultima rilevazione)
  getCurrentEstimate: (id) => api.get(`/plants/${id}/current-estimate`),
  // Controllo litri - Timeline unificata
  getLitersControl: (id, params) => api.get(`/plants/${id}/liters-control`, { params }),
  // Contatori dell'impianto (per inserimento letture)
  getMeters: (id) => api.get(`/plants/${id}/meters`),
  // Riepilogo AI impianto
  getAiSummary: (id) => api.get(`/plants/${id}/ai-summary`, { params: { cachedOnly: true } }),
  refreshAiSummary: (id) => api.post(`/plants/${id}/ai-summary/refresh`),
};

// API Manutenzioni
export const maintenancesApi = {
  getAll: (params) => api.get('/maintenances', { params }),
  getById: (id) => api.get(`/maintenances/${id}`),
  create: (data) => api.post('/maintenances', data),
  update: (id, data) => api.put(`/maintenances/${id}`, data),
  delete: (id) => api.delete(`/maintenances/${id}`),
  getUpcoming: (days = 30) => api.get('/maintenances/upcoming', { params: { days } }),
  getByPlant: (plantId) => api.get(`/maintenances/plant/${plantId}`),
};

// API Pratiche VVF
export const vvfPracticesApi = {
  getAll: (params) => api.get('/vvf-practices', { params }),
  getById: (id) => api.get(`/vvf-practices/${id}`),
  create: (data) => api.post('/vvf-practices', data),
  update: (id, data) => api.put(`/vvf-practices/${id}`, data),
  delete: (id) => api.delete(`/vvf-practices/${id}`),
  getExpiring: (days = 90) => api.get('/vvf-practices/expiring', { params: { days } }),
  getByPlant: (plantId) => api.get(`/vvf-practices/plant/${plantId}`),
  addDocument: (id, document) => api.put(`/vvf-practices/${id}/add-document`, { document }),
};

// API Contatori
export const metersApi = {
  getAll: (params) => api.get('/meters', { params }),
  getById: (id) => api.get(`/meters/${id}`),
  create: (data) => api.post('/meters', data),
  update: (id, data) => api.put(`/meters/${id}`, data),
  delete: (id) => api.delete(`/meters/${id}`),
  getByPlant: (plantId) => api.get(`/meters/by-plant/${plantId}`),
  getByCustomer: (customerId) => api.get(`/meters/by-customer/${customerId}`),
  bulkUpdatePrice: (data) => api.put('/meters/bulk-price', data),
  updateReading: (id, reading) => api.put(`/meters/${id}/reading`, { current_reading: reading }),
  getFilterOptions: () => api.get('/meters/filter-options'),
};

// API Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getRevenue: (params) => api.get('/dashboard/revenue', { params }),
  getTopCustomers: (limit = 10) => api.get('/dashboard/top-customers', { params: { limit } }),
  getCashFlow: (params) => api.get('/dashboard/cash-flow', { params }),
  getPlantsSummary: () => api.get('/dashboard/plants-summary'),
  redeploy: () => api.post('/dashboard/redeploy'),
};

// API Report
export const reportsApi = {
  getCustomerBalance: (customerId) => api.get(`/reports/customer-balance/${customerId}`),
  getOverdueInvoices: () => api.get('/reports/overdue'),
  getMonthlyRevenue: (year) => api.get('/reports/monthly-revenue', { params: { year } }),
  getCustomerStatement: (customerId, params) => 
    api.get(`/reports/customer-statement/${customerId}`, { params, responseType: 'blob' }),
  getReminder: (customerId, params) => 
    api.get(`/reports/reminder/${customerId}`, { params, responseType: 'blob' }),
};

// API Letture Contatori
export const meterReadingsApi = {
  // Ottieni contatori per inserimento letture (raggruppati per impianto)
  // Usa data_inizio e data_fine invece di anno/bimestre
  getForEntry: (params) => 
    api.get('/meter-readings/entry', { params }),
  // Salva batch di letture
  saveBatch: (data) => api.post('/meter-readings/batch', data),
  // Lista letture
  getAll: (params) => api.get('/meter-readings', { params }),
  // Dettaglio lettura
  getById: (id) => api.get(`/meter-readings/${id}`),
  // Modifica lettura
  update: (id, data) => api.put(`/meter-readings/${id}`, data),
  // Elimina/annulla lettura
  delete: (id) => api.delete(`/meter-readings/${id}`),
  // Statistiche per periodo (usa query params)
  getStats: (params) => api.get('/meter-readings/stats', { params }),
  // Storico letture per contatore
  getHistory: (meterId, params) => api.get(`/meter-readings/history/${meterId}`, { params }),
  // Storico letture per cliente
  getHistoryByCustomer: (customerId, params) => api.get(`/meter-readings/history-by-customer/${customerId}`, { params }),
  // Letture per fattura
  getHistoryByInvoice: (invoiceId) => api.get(`/meter-readings/history-by-invoice/${invoiceId}`),
  // Tabulato letture PDF
  getReadingSheet: (params) => api.get('/meter-readings/reading-sheet', { params, responseType: 'blob' }),
  // Tabulato letture - dati JSON per preview
  getReadingSheetData: (params) => api.get('/meter-readings/reading-sheet-data', { params }),
  // Export Excel/CSV Centro Servizi/SAP
  exportCentroServizi: (params) => api.get('/meter-readings/centro-servizi/export', { params, responseType: 'blob' }),
  // Preview import file letture Centro Servizi/SAP
  previewCentroServiziImport: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(options).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
    });
    return api.post('/meter-readings/centro-servizi/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Esecuzione import file letture Centro Servizi/SAP
  executeCentroServiziImport: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(options).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
    });
    return api.post('/meter-readings/centro-servizi/import/execute', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Sessioni rilevazione mobile (raggruppate per impianto+data+operatore)
  getMobileSessions: (params) => api.get('/meter-readings/mobile-sessions', { params }),
  // Dettaglio sessione mobile
  getMobileSessionDetail: (params) => api.get('/meter-readings/mobile-session-detail', { params }),
  // Convalida batch letture mobile
  validateBatch: (data) => api.put('/meter-readings/validate-batch', data),
  // Archivia/ripristina sessione mobile
  archiveSession: (data) => api.put('/meter-readings/archive-session', data),
  // Foto PWA per impianto
  getPlantPhotos: (plantId) => api.get(`/meter-readings/plant-photos/${plantId}`),
  // Foto PWA per contatore
  getMeterPhotos: (meterId) => api.get(`/meter-readings/meter-photos/${meterId}`),
  // Elimina foto PWA
  deletePhoto: (photoId) => api.delete(`/meter-readings/photos/${photoId}`),
  // Upload foto impianto (admin)
  uploadPlantPhoto: (plantId, formData) => api.post(`/meter-readings/upload-plant-photo/${plantId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Upload foto contatore (admin)
  uploadMeterPhoto: (meterId, formData) => api.post(`/meter-readings/upload-meter-photo/${meterId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// API Fatturazione
export const billingApi = {
  // Anteprima fatture da generare
  preview: (data) => api.post('/billing/preview', data),
  // Genera fatture
  generate: (data) => api.post('/billing/generate', data),
  // Lista emissioni (billing runs)
  getRuns: (params) => api.get('/billing/runs', { params }),
  // Dettaglio emissione
  getRunById: (id) => api.get(`/billing/runs/${id}`),
  // Lista periodi disponibili
  getPeriods: () => api.get('/billing/periods'),
  // Lista zone disponibili
  getZones: () => api.get('/billing/zones'),
};

// API Email
export const emailsApi = {
  // Lista email
  getEmails: (params) => api.get('/emails', { params }),
  // Dettaglio email
  getById: (id) => api.get(`/emails/${id}`),
  // Invia email
  send: (data) => api.post('/emails/send', data),
  // Rispondi
  reply: (id, data) => api.post(`/emails/${id}/reply`, data),
  // Inoltra
  forward: (id, data) => api.post(`/emails/${id}/forward`, data),
  // Elimina
  delete: (id) => api.delete(`/emails/${id}`),
  // Segna come letta/non letta
  markRead: (id, isRead) => api.patch(`/emails/${id}`, { is_read: isRead }),
  // Sync manuale tutti gli account + analisi AI
  syncAll: () => api.post('/emails/sync-all'),
  // Sync manuale (legacy)
  sync: () => api.post('/emails/sync'),
  // Suggerimenti clienti basati sul contenuto email
  suggestCustomers: (id) => api.get(`/emails/${id}/suggest-customers`),
  // Associa cliente a email
  assignCustomer: (id, customerId) => api.put(`/emails/${id}`, { customer_id: customerId }),
};

// ============================================
// API IMPORTAZIONE DATI
// ============================================

export const importApi = {
  // Lista entità importabili con campi richiesti
  getEntities: () => api.get('/import/entities'),
  
  // Scarica template CSV per entità
  downloadTemplate: (entity) => 
    api.get(`/import/template/${entity}`, { responseType: 'blob' }),
  
  // Auto-detect tipo CSV dagli headers
  detect: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/detect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Preview importazione (valida senza salvare)
  preview: (entity, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/preview/${entity}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Esegue importazione (supporta selezione righe)
  execute: (entity, file, mode = 'skip', selectedRows = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    if (selectedRows) {
      formData.append('selectedRows', JSON.stringify(selectedRows));
    }
    return api.post(`/import/execute/${entity}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Log delle importazioni
  getLogs: (params) => api.get('/import/logs', { params }),
  getLogById: (id) => api.get(`/import/logs/${id}`),
};

// ============================================
// API ESPORTAZIONE DATI CSV
// ============================================

export const exportApi = {
  // Lista entità esportabili
  getEntities: () => api.get('/export/entities'),
  
  // Esporta clienti
  exportCustomers: () => 
    api.get('/export/customers', { responseType: 'blob' }),
  
  // Esporta fatture (con filtri opzionali) - formato completo
  exportInvoices: (params = {}) => 
    api.get('/export/invoices', { params, responseType: 'blob' }),
  
  // Esporta fatture formato LEGACY (compatibile FoxPro - 48 colonne)
  exportInvoicesLegacy: (params = {}) => 
    api.get('/export/invoices/legacy', { params, responseType: 'blob' }),
  
  // Esporta fatture per fatturazione elettronica (formato Genioso)
  exportInvoicesElectronic: (params = {}) => 
    api.get('/export/invoices/electronic', { params, responseType: 'blob' }),

  // Report PDF resoconto fatture elettroniche esportate
  exportInvoicesElectronicReport: (params = {}) => 
    api.get('/export/invoices/electronic/report', { params, responseType: 'blob' }),
  
  // Esporta contatori
  exportMeters: () => 
    api.get('/export/meters', { responseType: 'blob' }),
  
  // Esporta impianti
  exportPlants: () => 
    api.get('/export/plants', { responseType: 'blob' }),
  
  // Esporta acconti
  exportAdvances: () => 
    api.get('/export/advances', { responseType: 'blob' }),
  
  // Esporta pagamenti
  exportPayments: () => 
    api.get('/export/payments', { responseType: 'blob' }),
};

// ============================================
// API AI MAIL ASSISTANT
// ============================================

export const aiMailApi = {
  // Stato del servizio AI
  getStatus: () => api.get('/ai-mail/status'),
  
  // Analizza singola email
  analyze: (emailId, options = {}) => {
    const payload = { emailId, ...options };
    console.log('🔍 [API DEBUG] aiMailApi.analyze called');
    console.log('🔍 [API DEBUG] emailId:', emailId, 'type:', typeof emailId);
    console.log('🔍 [API DEBUG] options:', options);
    console.log('🔍 [API DEBUG] Full payload:', JSON.stringify(payload));
    return api.post('/ai-mail/analyze', payload);
  },
  
  // Analizza batch di email
  analyzeBatch: (emailIds, options = {}) => 
    api.post('/ai-mail/analyze-batch', { emailIds, ...options }),
  
  // Ottieni analisi salvata (cache)
  getAnalysis: (emailId) => 
    api.get(`/ai-mail/${emailId}/analysis`),
  
  // Genera bozza risposta
  generateReply: (emailId, options = {}) => 
    api.post(`/ai-mail/${emailId}/generate-reply`, options),
  
  // Applica azione suggerita
  applyAction: (emailId, action, params = {}) => 
    api.post(`/ai-mail/${emailId}/apply-action`, { action, params }),

  // Dashboard & Action Queue
  getPendingActions: (params = {}) =>
    api.get('/ai-mail/pending', { params }),

  getPendingCount: () =>
    api.get('/ai-mail/pending/count'),

  getDashboardStats: () =>
    api.get('/ai-mail/dashboard/stats'),

  approveAction: (actionId, attachments, replyTo, cc) =>
    api.post(`/ai-mail/actions/${actionId}/approve`, {
      ...(attachments ? { attachments } : {}),
      ...(replyTo ? { replyTo } : {}),
      ...(cc ? { cc } : {}),
    }),

  rejectAction: (actionId) =>
    api.post(`/ai-mail/actions/${actionId}/reject`),

  bulkReject: (actionIds) =>
    api.post('/ai-mail/actions/bulk-reject', { actionIds }),

  editActionDraft: (actionId, data) =>
    api.put(`/ai-mail/actions/${actionId}/edit-draft`, data),

  regenerateReply: (actionId, customPrompt) =>
    api.post(`/ai-mail/actions/${actionId}/regenerate`, { customPrompt }),

  reanalyzeAction: (actionId) =>
    api.post(`/ai-mail/actions/${actionId}/reanalyze`),

  getCrmContext: (customerId) =>
    api.get(`/ai-mail/crm-context/${customerId}`),

  triggerAnalysis: (params = {}) => {
    console.log('🔍 [API] triggerAnalysis POST body:', JSON.stringify(params));
    return api.post('/ai-mail/trigger-analysis', params);
  },
};

// API Aziende (multi-company)
export const aziendeApi = {
  getAll: () => api.get('/aziende'),
  getById: (id) => api.get(`/aziende/${id}`),
  create: (data) => api.post('/aziende', data),
  update: (id, data) => api.put(`/aziende/${id}`, data),
  delete: (id) => api.delete(`/aziende/${id}`),
  setDefault: (id) => api.put(`/aziende/${id}/set-default`),
  uploadLogo: (id, formData) => api.post(`/aziende/${id}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: (id) => api.delete(`/aziende/${id}/logo`),
};

// API Documenti Impianto
export const plantDocumentsApi = {
  getAll: (plantId) => api.get(`/plant-documents/${plantId}`),
  upload: (plantId, files, nome, descrizione) => {
    const formData = new FormData();
    files.forEach(file => formData.append('documents', file));
    if (nome) formData.append('nome', nome);
    if (descrizione) formData.append('descrizione', descrizione);
    return api.post(`/plant-documents/${plantId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateGroup: (plantId, groupId, data) =>
    api.put(`/plant-documents/${plantId}/group/${groupId}`, data),
  download: (plantId, documentId) =>
    api.get(`/plant-documents/${plantId}/download/${documentId}`, { responseType: 'blob' }),
  view: (plantId, documentId) =>
    api.get(`/plant-documents/${plantId}/view/${documentId}`, { responseType: 'blob' }),
  deleteFile: (plantId, documentId) =>
    api.delete(`/plant-documents/${plantId}/file/${documentId}`),
  deleteGroup: (plantId, groupId) =>
    api.delete(`/plant-documents/${plantId}/group/${groupId}`),
};

// API Note Intervento (tabulato letture)
export const interventionNotesApi = {
  getAll: (params) => api.get('/intervention-notes', { params }),
  getByMeter: (meterId) => api.get('/intervention-notes', { params: { meter_id: meterId } }),
  getByPlant: (plantId) => api.get('/intervention-notes', { params: { plant_id: plantId, tipo: 'impianto' } }),
  getByPlantMeters: (plantId) => api.get(`/intervention-notes/by-plant-meters/${plantId}`),
  getByProvincia: (provincia) => api.get(`/intervention-notes/by-provincia/${provincia}`),
  create: (data) => api.post('/intervention-notes', data),
  update: (id, data) => api.put(`/intervention-notes/${id}`, data),
  complete: (id, data = {}) => api.patch(`/intervention-notes/${id}/complete`, data),
  reopen: (id) => api.patch(`/intervention-notes/${id}/reopen`),
  delete: (id) => api.delete(`/intervention-notes/${id}`),
};

// API Assistente Personale
export const assistantApi = {
  getSummary: () => api.get('/assistant/summary'),
  getTasks: (params = {}) => api.get('/assistant/tasks', { params }),
  getAssignableUsers: () => api.get('/assistant/assignable-users'),
  assignTask: (taskId, assigned_to_user_id) => api.patch(`/assistant/tasks/${taskId}/assign`, { assigned_to_user_id }),
  updateStatus: (taskId, data) => api.patch(`/assistant/tasks/${taskId}/status`, data),
  snoozeTask: (taskId, snoozed_until) => api.patch(`/assistant/tasks/${taskId}/snooze`, { snoozed_until }),
  backfill: (data = {}) => api.post('/assistant/backfill', data),
  getPushPublicKey: () => api.get('/assistant/push/public-key'),
  getPushStatus: () => api.get('/assistant/push/status'),
  subscribePush: (data) => api.post('/assistant/push/subscribe', data),
  unsubscribePush: (data) => api.post('/assistant/push/unsubscribe', data),
  testPush: () => api.post('/assistant/push/test'),
};

// API WhatsApp
export const whatsappApi = {
  getStatus: (lineId = null) => api.get('/whatsapp/status', { params: lineId ? { line_id: lineId } : {} }),
  getLines: () => api.get('/whatsapp/lines'),
  createLine: (data) => api.post('/whatsapp/lines', data),
  updateLine: (id, data) => api.put(`/whatsapp/lines/${id}`, data),
  deleteLine: (id) => api.delete(`/whatsapp/lines/${id}`),
  getQr: (lineId = null) => api.get(lineId ? `/whatsapp/lines/${lineId}/qr` : '/whatsapp/qr', { params: lineId ? {} : {} }),
  initialize: (lineId = null) => lineId ? api.post(`/whatsapp/lines/${lineId}/initialize`) : api.post('/whatsapp/initialize'),
  logout: (lineId = null) => lineId ? api.post(`/whatsapp/lines/${lineId}/logout`) : api.post('/whatsapp/logout'),
  restart: (lineId = null) => lineId ? api.post(`/whatsapp/lines/${lineId}/restart`) : api.post('/whatsapp/restart'),
  syncChats: (data = {}) => api.post('/whatsapp/sync', data),
  checkNumber: (phone, lineId = null) => api.get(`/whatsapp/check-number/${encodeURIComponent(phone)}`, { params: lineId ? { line_id: lineId } : {} }),
  getConversations: (params = {}) => api.get('/whatsapp/conversations', { params }),
  getConversation: (identifier, params) => api.get(`/whatsapp/conversations/${identifier}`, { params }),
  fetchMessages: (phone, limit, lineId = null) => api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/fetch`, {}, { params: { limit, ...(lineId ? { line_id: lineId } : {}) } }),
  sendMessage: (data) => api.post('/whatsapp/send', data),
  sendMedia: (formData) => api.post('/whatsapp/send-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadMedia: (waMessageId, lineId = null) => api.post(`/whatsapp/messages/${encodeURIComponent(waMessageId)}/download-media`, {}, { params: lineId ? { line_id: lineId } : {} }),
  markRead: (id) => api.patch(`/whatsapp/messages/${id}/read`),
  getUnreadCount: (lineId = null) => api.get('/whatsapp/unread-count', { params: lineId ? { line_id: lineId } : {} }),
  assignCustomer: (phone, customerId, lineId = null) => api.put(`/whatsapp/conversations/${encodeURIComponent(phone)}/customer`, { customer_id: customerId, ...(lineId ? { line_id: lineId } : {}) }),
  removeCustomer: (phone, lineId = null) => api.put(`/whatsapp/conversations/${encodeURIComponent(phone)}/customer`, { customer_id: null, ...(lineId ? { line_id: lineId } : {}) }),
  suggestCustomers: (phone, lineId = null) => api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}/suggest-customers`, { params: lineId ? { line_id: lineId } : {} }),
  getProfilePic: (phone) => `${getMediaBaseUrl()}/whatsapp-avatars/${phone.replace(/[^0-9]/g, '')}.jpg`,
  // Contatti nascosti
  hideContact: (phone, data = {}) => api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/hide`, data),
  unhideContact: (phone) => api.delete(`/whatsapp/conversations/${encodeURIComponent(phone)}/hide`),
  getHiddenContacts: () => api.get('/whatsapp/hidden-contacts'),
  // Phone matching
  getPhoneMatching: () => api.get('/whatsapp/phone-matching'),
  autoLinkMatches: (matches) => api.post('/whatsapp/phone-matching/auto-link', { matches }),
};

// AI WhatsApp Assistant
export const aiWhatsappApi = {
  getStatus: () => api.get('/ai-whatsapp/status'),
  analyze: (phone, options = {}) => api.post(`/ai-whatsapp/analyze/${encodeURIComponent(phone)}`, options),
  generateReply: (phone, options = {}) => api.post(`/ai-whatsapp/${encodeURIComponent(phone)}/generate-reply`, options),
  getLatestAction: (phone) => api.get(`/ai-whatsapp/actions/${encodeURIComponent(phone)}/latest`),
  getPendingActions: () => api.get('/ai-whatsapp/actions/pending'),
  editDraft: (actionId, reply_draft) => api.put(`/ai-whatsapp/actions/${actionId}/draft`, { reply_draft }),
  approveAction: (actionId, attachments) => api.post(`/ai-whatsapp/actions/${actionId}/approve`, { attachments }),
  rejectAction: (actionId) => api.post(`/ai-whatsapp/actions/${actionId}/reject`),
  regenerateReply: (actionId, customPrompt, tone) => api.post(`/ai-whatsapp/actions/${actionId}/regenerate`, { customPrompt, tone }),
};

// Comunicazioni (pagine da preporre ai PDF fattura)
export const comunicazioniApi = {
  getAll: (params) => api.get('/communications', { params }),
  getById: (id) => api.get(`/communications/${id}`),
  create: (data) => api.post('/communications', data),
  update: (id, data) => api.put(`/communications/${id}`, data),
  delete: (id) => api.delete(`/communications/${id}`),
  toggle: (id) => api.patch(`/communications/${id}/toggle`),
  forInvoice: (invoiceId) => api.get(`/communications/for-invoice/${invoiceId}`),
};

export default api;
